import React, { useEffect, useState } from 'react';
import { Button, Modal, Form, Input, message, Spin, Row, Col } from 'antd';
import {
  PlusOutlined,
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { CreateEmployeeRequest } from '../../model/Employee.model';
import RoleService from '../../service/auth-service/Role.service';
import EmployeeService from '../../service/employee-service/Employee.service';

interface CreateEmployeeModalProps {
  onSuccess: () => void;
}

const CreateEmployeeModal: React.FC<CreateEmployeeModalProps> = ({
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [employeeRoleId, setEmployeeRoleId] = useState<number | null>(null);

  const fetchEmployeeRoleId = async () => {
    try {
      const roles = await RoleService.getRoles();
      const employeeRole = roles.find(
        (role: { id: number; name: string }) => role.name === 'ROLE_EMPLOYEE'
      );
      if (employeeRole) {
        setEmployeeRoleId(employeeRole.id);
      } else {
        message.error('Không tìm thấy vai trò nhân viên');
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      message.error('Không thể tải thông tin vai trò');
    }
  };

  useEffect(() => {
    fetchEmployeeRoleId();
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (isModalOpen) {
      form.resetFields();
    }
  }, [isModalOpen, form]);

  const validateUsername = (_: any, value: string) => {
    const regex = /^[a-z0-9]+$/;

    if (!value) {
      return Promise.reject('Tên đăng nhập không được để trống');
    }

    if (!regex.test(value)) {
      if (/[A-Z]/.test(value)) {
        return Promise.reject('Tên đăng nhập không được chứa chữ viết hoa');
      } else if (/\s/.test(value)) {
        return Promise.reject('Tên đăng nhập không được chứa khoảng trắng');
      } else if (/[_.-]/.test(value)) {
        return Promise.reject('Tên đăng nhập không được chứa các ký tự _ . -');
      } else {
        return Promise.reject('Tên đăng nhập chỉ được chứa chữ thường và số');
      }
    }

    return Promise.resolve();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsLoading(true);

      if (!employeeRoleId) {
        throw new Error('Chưa tải được thông tin vai trò nhân viên');
      }

      const formData: CreateEmployeeRequest = {
        username: values.username,
        password: values.password,
        fullName: values.fullName,
        email: values.email || null,
        phone: values.phone || null,
        position: '', // Removed as per requirement
        roleId: employeeRoleId, // Always use ROLE_EMPLOYEE
      };

      await EmployeeService.createEmployee(formData);
      message.success('Đăng ký nhân viên thành công!');
      onSuccess();
      setIsModalOpen(false);
      form.resetFields();
    } catch (error: any) {
      if (error.message) {
        message.error(error.message);
      } else {
        message.error('Đăng ký thất bại');
      }
      console.error('Error creating employee:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        type="default"
        variant="filled"
        color="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          form.resetFields();
          setIsModalOpen(true);
        }}
      >
        Đăng ký nhân viên mới
      </Button>

      <Modal
        title="Đăng ký nhân viên mới"
        open={isModalOpen}
        onCancel={() => {
          form.resetFields();
          setIsModalOpen(false);
        }}
        footer={null}
        width={800}
      >
        <Spin spinning={isLoading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            className="mt-4"
            autoComplete="off"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="username"
                  label="Tên tài khoản"
                  rules={[
                    { required: true, message: 'Vui lòng nhập tên tài khoản' },
                    { validator: validateUsername },
                  ]}
                  help="Chỉ được chứa chữ thường và số"
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Nhập tên tài khoản"
                    autoComplete="off"
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="password"
                  label="Mật khẩu"
                  rules={[
                    { required: true, message: 'Vui lòng nhập mật khẩu' },
                    { min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự' },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Nhập mật khẩu"
                    autoComplete="new-password"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16} className="mt-4">
              <Col span={12}>
                <Form.Item
                  name="fullName"
                  label="Tên nhân viên"
                  rules={[
                    { required: true, message: 'Vui lòng nhập họ và tên' },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Nhập họ và tên"
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[{ type: 'email', message: 'Email không hợp lệ' }]}
                >
                  <Input prefix={<MailOutlined />} placeholder="Nhập email" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="phone"
                  label="Điện thoại"
                  rules={[
                    {
                      pattern: /^\d{10,11}$/,
                      message: 'Số điện thoại không hợp lệ',
                    },
                  ]}
                >
                  <Input
                    prefix={<PhoneOutlined />}
                    placeholder="Nhập số điện thoại"
                  />
                </Form.Item>
              </Col>
            </Row>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                onClick={() => {
                  form.resetFields();
                  setIsModalOpen(false);
                }}
              >
                Huỷ
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Hoàn thành
              </Button>
            </div>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

export default CreateEmployeeModal;
